import { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import useAuth from "../../../hooks/useAuth";
import GoogleIcon from "../../common/GoogleIcon/GoogleIcon";
import Logo from "../../common/Logo/Logo";
import PageContainer from "../../common/PageContainer/PageContainer";
import styles from "./Welcome.module.css";

const PROMISES = [
  {
    icon: "fa-solid fa-comment-dots",
    title: "What just happened?",
    body: "Tap any play and get a plain-English answer. No rulebook, no jargon.",
  },
  {
    icon: "fa-solid fa-people-group",
    title: "Watch with friends",
    body: "Share a room link, make silly predictions, and see who actually knows football.",
  },
  {
    icon: "fa-solid fa-brain",
    title: "Learn without studying",
    body: "You pick things up as the game goes. Your Football IQ goes up on its own.",
  },
];

/**
 * The front door.
 *
 * Guest mode is first-class and is the primary button. Google sign-in is
 * offered but never required, and we never block the product behind it.
 */
const Welcome = () => {
  const navigate = useNavigate();
  const {
    status,
    continueAsGuest,
    signInWithGoogle,
    pendingAction,
    error,
    clearError,
    isLocalMode,
    authReady,
    ensureAuthReady,
  } = useAuth();

  useEffect(() => {
    // Clear a stale error when the screen is revisited.
    return () => clearError();
  }, [clearError]);

  useEffect(() => {
    if (!authReady) ensureAuthReady();
  }, [authReady, ensureAuthReady]);

  if (status === "signed-in") {
    return <Navigate to="/home" replace />;
  }

  const busy = Boolean(pendingAction);

  const handleGuest = async () => {
    const result = await continueAsGuest();
    if (result) navigate("/home", { replace: true });
  };

  const handleGoogle = async () => {
    const result = await signInWithGoogle();
    if (result) navigate("/home", { replace: true });
  };

  return (
    <PageContainer>
      <div className={styles.Welcome}>
        <Logo size="lg" className="mb-3" />

        <h1 className={styles.headline}>
          Football explained for people who don&rsquo;t know football.
        </h1>
        <p className={styles.sub}>
          Everyone else is yelling about 4th down. You&rsquo;re allowed to have
          no idea what that means. We&rsquo;ll fill you in as the game goes.
        </p>

        <div className={styles.actions}>
          <button
            type="button"
            className="btn btn-primary btn-lg w-100"
            onClick={handleGuest}
            disabled={busy}
          >
            {pendingAction === "guest" ? "Setting you up…" : "Start watching"}
          </button>
          <p className={styles.noAccount}>
            No account needed. We&rsquo;ll give you a nickname.
          </p>

          <button
            type="button"
            className="btn btn-outline-secondary w-100"
            onClick={handleGoogle}
            disabled={busy}
          >
            <GoogleIcon className="me-2" />
            {pendingAction === "google" ? "Opening Google…" : "Continue with Google"}
          </button>
        </div>

        {error && (
          <div className={styles.error} role="alert">
            <i className="fa-solid fa-circle-exclamation me-2" aria-hidden="true" />
            {error.message}
          </div>
        )}

        {isLocalMode && (
          <p className={styles.localNote}>
            Development build: sign-in is simulated locally because Firebase
            isn&rsquo;t configured in this environment.
          </p>
        )}

        <ul className={styles.promises}>
          {PROMISES.map((promise) => (
            <li key={promise.title} className={styles.promise}>
              <span className={styles.promiseIcon} aria-hidden="true">
                <i className={promise.icon} />
              </span>
              <div>
                <p className={styles.promiseTitle}>{promise.title}</p>
                <p className={styles.promiseBody}>{promise.body}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </PageContainer>
  );
};

export default Welcome;
